import { format as formatDate } from 'date-fns';
import { fetchTimezoneIdByOlsonName } from './api';
import { MarketingConsent } from '../classes/MarketingConsent';

/**
 * Gets timezone key using Olson timezone name with fallback to browser offset
 */
export async function getTimezoneKeyByOlsonName(timeZones) {
  // Check if Intl API is supported
  if (
    typeof Intl !== 'undefined' &&
    Intl.DateTimeFormat &&
    Intl.DateTimeFormat().resolvedOptions
  ) {
    try {
      const olsonName = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (olsonName) {
        const timezoneId = await fetchTimezoneIdByOlsonName(olsonName);
        if (timezoneId && timeZones[timezoneId]) {
          return Number(timezoneId);
        }
      }
    } catch (error) {
      console.warn(
        'Failed to get timezone by Olson name, falling back to browser offset:',
        error,
      );
    }
  }

  // Fallback to the original implementation
  return getTimezoneKeyByBrowser(timeZones);
}

/**
 * Gets timezone key based on browser's current timezone (fallback method)
 */
export function getTimezoneKeyByBrowser(timeZones) {
  const currentTimezone = formatDate(new Date(), 'xxx');

  for (const key in timeZones) {
    if (timeZones[key].includes(currentTimezone)) {
      return Number(key);
    }
  }
  return null;
}

/**
 * Returns a MarketingConsent object if SMS consent registration is enabled and a template ID is present.
 * Will return a default MarketingConsent with registration disabled if either condition is not met.
 *
 * @param {Object} consentData - The consent data object.
 * @param {boolean} consentData.is_sms_consent_registration_enabled - Whether SMS consent registration is enabled.
 * @param {number|string|null} consentData.consent_template_id - The consent template ID.
 * @param {string} [consentData.marketing_consent_text] - The marketing consent text.
 * @param {Array<string>} [consentData.countries_requiring_consent] - List of countries requiring consent.
 * @returns {MarketingConsent} The MarketingConsent object.
 */
export function getMarketingConsent(consentData) {
  if (
    consentData.is_sms_consent_registration_enabled !== true ||
    consentData.consent_template_id == null
  ) {
    // do not expose consent properties if registration is not enabled or template ID is missing
    return MarketingConsent.Default;
  }

  return new MarketingConsent({
    isSmsConsentRegistrationEnabled:
      consentData.is_sms_consent_registration_enabled,
    consentTemplateId: consentData.consent_template_id,
    marketingConsentText: consentData.marketing_consent_text,
    countriesRequiringConsent: consentData.countries_requiring_consent,
  });
}

/**
 * Extracts country code from Umbraco regionalization data
 */
export function getCountryByRegionalization() {
  const regionalizationElement = document.querySelector(
    '[data-umbraco-regionalization]',
  );
  if (!regionalizationElement) return null;

  try {
    const regionalization = JSON.parse(
      regionalizationElement.dataset.umbracoRegionalization,
    );
    return regionalization?.Country || null;
  } catch {
    return null;
  }
}

/**
 * Gets country name by country code
 */
export function getCountryNameByCode(countryCode, countries = []) {
  if (!countryCode || !Array.isArray(countries)) return '';

  const country = countries.find((c) => c.country_code === countryCode);
  return country?.country_name || '';
}

/**
 * Gets currency code by country code
 */
export function getCurrencyCodeByCountryCode(countryCode, countries = []) {
  if (!countryCode || !Array.isArray(countries)) return '';

  const country = countries.find((c) => c.country_code === countryCode);
  return country?.currency_code || '';
}

/**
 * Gets referral ID from session storage or URL parameters
 */
export function getReferralId() {
  if (typeof window === 'undefined') return '';

  const sessionReferralId = window.sessionStorage.getItem('referralId');
  if (sessionReferralId) return sessionReferralId;

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('referralId') || '';
}
